/* eslint-disable no-throw-literal */
import gql from "graphql-tag";
import apolloClient from "../../../../utils/apolloClient";

// ─── GraphQL Mutation ─────────────────────────────────────────────────────────

const GENERATE_RCTI_INVOICE = gql`
  mutation GenerateRctiInvoice($input: GenerateRctiInvoiceInput) {
    generate_rcti_invoice(input: $input) {
      invoice_url
      error {
        code
        description
      }
    }
  }
`;

// ─── Pure Transformer ─────────────────────────────────────────────────────────

const toRctiResult = raw => ({
  invoiceUrl: raw?.invoice_url ?? null,
});

// ─── Error Normalizer ─────────────────────────────────────────────────────────

const isAppError = err =>
  typeof err === "object" && err !== null && "code" in err && "message" in err;

const toAppError = err => {
  if (err instanceof Error) {
    const msg = err.message.toLowerCase();
    if (msg.includes("unauthorized") || msg.includes("unauthenticated")) {
      return { message: err.message, code: "UNAUTHORIZED" };
    }
    if (msg.includes("network") || msg.includes("failed to fetch")) {
      return { message: err.message, code: "NETWORK_ERROR" };
    }
    return { message: err.message, code: "UNKNOWN_ERROR" };
  }
  return { message: "An unexpected error occurred", code: "UNKNOWN_ERROR" };
};

// ─── Service Implementation ───────────────────────────────────────────────────

export async function generateRctiInvoice(
  params,
  client = apolloClient.clientPrivate,
) {
  const { placeId, fromDate, toDate } = params;

  if (!placeId) {
    throw {
      message: "placeId is required to generate an RCTI invoice",
      code: "MISSING_PLACE_ID",
    };
  }

  try {
    const { data } = await client.mutate({
      mutation: GENERATE_RCTI_INVOICE,
      variables: {
        input: {
          place_id: placeId,
          from_date: fromDate,
          to_date: toDate,
        },
      },
    });

    const result = data?.generate_rcti_invoice;

    if (!result) {
      throw {
        message: "No data returned from generate_rcti_invoice",
        code: "PARSE_ERROR",
      };
    }

    if (result.error?.length) {
      throw {
        message: result.error[0]?.description ?? "API error",
        code: "API_ERROR",
      };
    }

    return toRctiResult(result);
  } catch (err) {
    if (isAppError(err)) throw err;
    throw toAppError(err);
  }
}

// ─── Export Pure Transformer ──────────────────────────────────────────────────

export { toRctiResult };

// ─── Export Service ───────────────────────────────────────────────────────────

export const generateRctiInvoiceService = {
  generateRctiInvoice,
};
